import React, { useEffect, useRef, useState } from 'react';
import { TextInput, TextInputProps, useDataProvider, useNotify, useInput, usePermissions } from 'react-admin';
import { PERMISSIONS } from '../../constants';
import { Box, IconButton, InputAdornment, CircularProgress, Tooltip, Typography } from '@mui/material';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import FeatureIntro from '../FeatureIntro';

const FEATURE_INTRO_ID = 'ai-text-input-rephrase';

interface AiTextInputProps extends TextInputProps {
  fieldType?: string;
}

const AiTextInput = ({ fieldType, InputProps, ...props }: AiTextInputProps) => {
  const { field } = useInput(props);
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const { permissions } = usePermissions();
  const [loading, setLoading] = useState(false);
  const [showPulse, setShowPulse] = useState(false);
  const pulseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasPermission = permissions?.includes(PERMISSIONS.TEXT_EDITOR_AI);

  useEffect(() => {
    if ((field.value?.trim()?.length || 0) < 10) return;
    if (pulseTimerRef.current) clearTimeout(pulseTimerRef.current);
    pulseTimerRef.current = setTimeout(() => {
      setShowPulse(true);
      pulseTimerRef.current = setTimeout(() => setShowPulse(false), 3000);
    }, 800);
    return () => { if (pulseTimerRef.current) clearTimeout(pulseTimerRef.current); };
  }, [field.value]);
  const showButton = hasPermission && (field.value?.trim()?.length || 0) >= 10;

  const handleRephrase = async () => {
    const text = field.value;
    if (!text || text.trim().length === 0) {
      notify('Não há texto para processar.', { type: 'warning' });
      return;
    }

    setLoading(true);
    try {
      const response = await dataProvider.create('chains/chainId/ai-tools/text-editor', {
        data: { text, action: 'rephrase', fieldType: fieldType ?? 'plain_text' },
      });

      const result = response.data as any;
      if (result.success && result.result) {
        const cleaned = result.result.replace(/<[^>]*>/g, '').trim();
        field.onChange({ target: { value: cleaned } });
        notify('Texto refraseado com sucesso!', { type: 'success' });
      } else {
        notify(result.error || 'Erro ao processar texto.', { type: 'error' });
      }
    } catch (error) {
      console.error('[AiTextInput] Error:', error);
      notify('Erro ao processar texto. Tente novamente.', { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <TextInput
      {...props}
      InputProps={{
        ...InputProps,
        endAdornment: showButton ? (
          <InputAdornment position="end">
            <FeatureIntro
              id={FEATURE_INTRO_ID}
              title="Refrasear com IA"
              description="Melhore o texto automaticamente com inteligência artificial. Clique no ícone para reescrever o campo de forma mais envolvente."
            >
              <Tooltip
                title={
                  <Box>
                    <Typography fontSize="0.85rem" fontWeight={700} gutterBottom>✨ Refrasear com IA</Typography>
                    <Typography fontSize="0.78rem" color="text.secondary">Reescreve o texto de forma mais envolvente e natural, mantendo o mesmo significado.</Typography>
                  </Box>
                }
                arrow
                slotProps={{
                  tooltip: {
                    sx: {
                      bgcolor: 'background.paper',
                      color: 'text.primary',
                      boxShadow: 6,
                      borderRadius: 2,
                      p: 1.5,
                      fontSize: '0.82rem',
                      fontWeight: 500,
                      border: '1px solid',
                      borderColor: 'divider',
                    },
                  },
                  arrow: { sx: { color: 'background.paper' } },
                }}
              >
                <Box sx={{
                  position: 'relative',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  '@keyframes radar-pulse': {
                    '0%': { transform: 'scale(1)', opacity: 0.6 },
                    '100%': { transform: 'scale(2.4)', opacity: 0 },
                  },
                }}>
                  {showPulse && <>
                    <Box sx={{ position: 'absolute', top: -5, left: -5, right: -5, bottom: -5, borderRadius: '50%', border: '2px solid #667eea', animation: 'radar-pulse 2s ease-out infinite', pointerEvents: 'none' }} />
                    <Box sx={{ position: 'absolute', top: -5, left: -5, right: -5, bottom: -5, borderRadius: '50%', border: '2px solid #667eea', animation: 'radar-pulse 2s ease-out 0.8s infinite', pointerEvents: 'none' }} />
                  </>}
                  <IconButton
                    onClick={handleRephrase}
                    disabled={loading || !field.value?.trim()}
                    size="small"
                    sx={{
                      color: '#667eea',
                      '&:hover': { bgcolor: 'rgba(102, 126, 234, 0.08)' },
                    }}
                  >
                    {loading ? <CircularProgress size={18} color="inherit" /> : <AutoFixHighIcon fontSize="small" />}
                  </IconButton>
                </Box>
              </Tooltip>
            </FeatureIntro>
          </InputAdornment>
        ) : InputProps?.endAdornment,
      }}
    />
  );
};

export default AiTextInput;
